/**
 * LYT Communications - Daily Production Report & Submission
 * Version: 2.0.0
 * Updated: 2026-03-04
 * Route: #daily-report
 *
 * Track 1: Daily production submission to client.
 * Generates daily production reports from QC-approved segment data.
 * Emails report to client (Vexus, Highline, IE Smart Systems, etc.), tracks approval and payment status.
 * Includes billing totals from rate card matching.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Printer, Calendar, Filter, Loader, FileText, DollarSign,
  Ruler, Users, MapPin, CheckCircle, Clock, AlertTriangle, Sun, Moon,
  Send, Eye, Check, CreditCard, ChevronDown, ChevronUp, X, Mail
} from 'lucide-react';
import {
  loadFullProject, loadProductionSubmissions, createProductionSubmission,
  updateSubmissionField, markSegmentsBilled, fetchFullRates
} from '../services/mapService';
import { sendEmailNotification } from '../services/notificationService';
import { getBoringCode, getPullingCode } from '../config/mapConfig';
import { getClientConfig } from '../config/constants';

function DailyProductionReport({ darkMode, setDarkMode, user, setCurrentPage, projectId }) {
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [splicePoints, setSplicePoints] = useState([]);
  const [project, setProject] = useState(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [filterContractor, setFilterContractor] = useState('all');
  const [filterSection, setFilterSection] = useState('all');
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [showVersion, setShowVersion] = useState(false);

  // Submission state
  const [activeTab, setActiveTab] = useState('report'); // report | submissions
  const [submissions, setSubmissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  // Approval/Payment modals
  const [approvalModal, setApprovalModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentInvNum, setPaymentInvNum] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const border = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const muted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    async function load() {
      if (!projectId) { setLoading(false); return; }
      try {
        const [data, subs] = await Promise.all([
          loadFullProject(projectId),
          loadProductionSubmissions(projectId),
        ]);
        setSegments(data.segments || []);
        setSplicePoints(data.splicePoints || []);
        setProject(data.project || null);
        setSubmissions(subs || []);
      } catch (err) {
        console.error('Failed to load project data:', err);
      }
      setLoading(false);
    }
    load();
  }, [projectId]);

  // Get unique contractors and sections
  const contractors = useMemo(() => {
    const set = new Set();
    segments.forEach(s => {
      if (s.boring_assigned_to) set.add(s.boring_assigned_to);
      if (s.pulling_assigned_to) set.add(s.pulling_assigned_to);
    });
    return [...set].sort();
  }, [segments]);

  const sections = useMemo(() => [...new Set(segments.map(s => s.section).filter(Boolean))].sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0)), [segments]);

  const clientConfig = getClientConfig(project?.client);
  const clientName = clientConfig.companyName || project?.client || 'Client';
  const rateCardId = clientConfig?.rateCard || 'vexus-la-tx-2026';

  // Client billing rates — fetched from server (key-gated), never in the bundle
  const [fullRateInfo, setFullRateInfo] = useState(null);
  useEffect(() => {
    let live = true;
    fetchFullRates(rateCardId)
      .then(d => { if (live && d) setFullRateInfo(d); })
      .catch(err => console.error('Failed to load rates:', err));
    return () => { live = false; };
  }, [rateCardId]);
  const rates = fullRateInfo?.rates || {};
  const priceField = fullRateInfo?.priceField || 'client_rate';

  // Segments with QC-approved work that has NOT been billed yet
  const billableSegments = useMemo(() => {
    return segments.filter(seg => {
      const boringBillable = seg.boring_status === 'QC Approved' && (!seg.boring_billing_status || seg.boring_billing_status === 'Not Billed');
      const pullingBillable = seg.pulling_status === 'QC Approved' && (!seg.pulling_billing_status || seg.pulling_billing_status === 'Not Billed');

      if (!boringBillable && !pullingBillable) return false;

      if (filterContractor !== 'all') {
        const match = (seg.boring_assigned_to || '').includes(filterContractor) || (seg.pulling_assigned_to || '').includes(filterContractor);
        if (!match) return false;
      }
      if (filterSection !== 'all' && seg.section !== filterSection) return false;

      return true;
    });
  }, [segments, filterContractor, filterSection]);

  // Generate line items from billable segments
  const lineItems = useMemo(() => {
    const items = [];
    billableSegments.forEach(seg => {
      const footage = parseFloat(seg.boring_actual_footage) || parseFloat(seg.footage) || 0;
      const boringBillable = seg.boring_status === 'QC Approved' && (!seg.boring_billing_status || seg.boring_billing_status === 'Not Billed');
      const pullingBillable = seg.pulling_status === 'QC Approved' && (!seg.pulling_billing_status || seg.pulling_billing_status === 'Not Billed');

      if (boringBillable && footage > 0) {
        if (seg.vendor_codes && Array.isArray(seg.vendor_codes) && seg.vendor_codes.length > 0) {
          // Bastrop path: one line item per vendor code (base + adders)
          for (const vc of seg.vendor_codes) {
            if (!vc.code) continue;
            const rate = rates?.[vc.code]?.[priceField] || 0;
            items.push({
              segment_id: seg.segment_id,
              phase: 'boring',
              code: vc.code,
              description: `${vc.description || vc.code} — ${seg.street || seg.segment_id} (${footage} ${vc.uom || 'LF'})`,
              qty: footage,
              uom: vc.uom || 'LF',
              rate,
              total: Math.round(footage * rate * 100) / 100,
              section: seg.section,
              contractor: seg.boring_assigned_to,
            });
          }
        } else {
          // Vexus/fallback path: derive code from getBoringCode()
          const ductCount = parseInt(seg.duct_count) || 1;
          const code = getBoringCode(rateCardId, ductCount, seg.bore_code, seg.cover_depth);
          const rate = rates?.[code]?.[priceField] || 0;

          items.push({
            segment_id: seg.segment_id,
            phase: 'boring',
            code,
            description: `Boring — ${seg.street || seg.segment_id} (${footage} LF)`,
            qty: footage,
            uom: 'LF',
            rate,
            total: Math.round(footage * rate * 100) / 100,
            section: seg.section,
            contractor: seg.boring_assigned_to,
          });
        }
      }

      if (pullingBillable && footage > 0) {
        const ct = parseInt(seg.pulling_cable_type) || parseInt(seg.cable_type) || 24;
        const code = getPullingCode(rateCardId, ct);
        const rate = rates?.[code]?.[priceField] || 0;

        items.push({
          segment_id: seg.segment_id,
          phase: 'pulling',
          code,
          description: `Pulling ${ct}F — ${seg.street || seg.segment_id} (${footage} LF)`,
          qty: footage,
          uom: 'LF',
          rate,
          total: Math.round(footage * rate * 100) / 100,
          section: seg.section,
          contractor: seg.pulling_assigned_to,
        });
      }
    });
    return items;
  }, [billableSegments, rateCardId, rates, priceField]);

  const subtotal = useMemo(() => lineItems.reduce((sum, li) => sum + li.total, 0), [lineItems]);
  const totalFootage = useMemo(() => lineItems.reduce((sum, li) => sum + li.qty, 0), [lineItems]);

  // Filter segments that had activity on the selected date (original report view)
  const activeSegments = useMemo(() => {
    const dateStr = reportDate;
    return segments.filter(seg => {
      const matchDate = (ts) => ts && ts.toString().slice(0, 10) === dateStr;
      const boringActive = matchDate(seg.boring_started) || matchDate(seg.boring_completed) || matchDate(seg.boring_qc_approved_date);
      const pullingActive = matchDate(seg.pulling_started) || matchDate(seg.pulling_completed) || matchDate(seg.pulling_qc_approved_date);
      const boringInProg = seg.boring_status === 'In Progress';
      const pullingInProg = seg.pulling_status === 'In Progress';
      const hasActivity = boringActive || pullingActive || boringInProg || pullingInProg;
      if (filterContractor !== 'all') {
        const match = (seg.boring_assigned_to || '').includes(filterContractor) || (seg.pulling_assigned_to || '').includes(filterContractor);
        if (!match) return false;
      }
      if (filterSection !== 'all' && seg.section !== filterSection) return false;
      return hasActivity;
    });
  }, [segments, reportDate, filterContractor, filterSection]);

  const activeSplices = useMemo(() => {
    const dateStr = reportDate;
    return splicePoints.filter(sp => {
      const matchDate = (ts) => ts && ts.toString().slice(0, 10) === dateStr;
      return matchDate(sp.started) || matchDate(sp.completed) || matchDate(sp.qc_approved_date) || sp.status === 'In Progress';
    });
  }, [splicePoints, reportDate]);

  const summary = useMemo(() => {
    let boringFootage = 0, pullingFootage = 0, boringSegments = 0, pullingSegments = 0;
    let boringValue = 0, pullingValue = 0, spliceValue = 0;
    activeSegments.forEach(seg => {
      const footage = parseFloat(seg.footage) || 0;
      if (['Complete', 'QC Approved', 'In Progress'].includes(seg.boring_status)) {
        const ft = parseFloat(seg.boring_actual_footage) || footage;
        boringFootage += ft;
        boringSegments++;
        if (seg.vendor_codes && Array.isArray(seg.vendor_codes) && seg.vendor_codes.length > 0) {
          for (const vc of seg.vendor_codes) {
            if (!vc.code) continue;
            boringValue += ft * (rates?.[vc.code]?.[priceField] || 0);
          }
        } else {
          const code = getBoringCode(rateCardId, parseInt(seg.duct_count) || 1, seg.bore_code, seg.cover_depth);
          boringValue += ft * (rates?.[code]?.[priceField] || 0);
        }
      }
      if (['Complete', 'QC Approved', 'In Progress'].includes(seg.pulling_status)) {
        pullingFootage += footage;
        pullingSegments++;
        const ct = parseInt(seg.pulling_cable_type) || parseInt(seg.cable_type) || 24;
        const code = getPullingCode(rateCardId, ct);
        pullingValue += footage * (rates?.[code]?.[priceField] || 0);
      }
    });
    activeSplices.forEach(() => {
      // Splice summary value uses a rough per-splice estimate; detailed billing is on PaymentInvoicePage
      spliceValue += rates?.['FS1']?.[priceField] || rates?.['HL-SPL02']?.[priceField] || 0;
    });
    return {
      boringFootage: Math.round(boringFootage), pullingFootage: Math.round(pullingFootage),
      boringSegments, pullingSegments, spliceCount: activeSplices.length,
      boringValue: Math.round(boringValue * 100) / 100,
      pullingValue: Math.round(pullingValue * 100) / 100,
      spliceValue: Math.round(spliceValue * 100) / 100,
      totalValue: Math.round((boringValue + pullingValue + spliceValue) * 100) / 100,
    };
  }, [activeSegments, activeSplices, rateCardId, rates, priceField]);

  // === ACTIONS ===

  const handleSubmitToClient = async () => {
    if (lineItems.length === 0) { showToast('No billable segments to submit', 'error'); return; }
    if (!clientEmail) { showToast('Enter client email address', 'error'); return; }

    setSubmitting(true);
    try {
      const boringSegIds = lineItems.filter(li => li.phase === 'boring').map(li => li.segment_id);
      const pullingSegIds = lineItems.filter(li => li.phase === 'pulling').map(li => li.segment_id);
      const allSegIds = [...new Set([...boringSegIds, ...pullingSegIds])];

      // Create the submission record
      const submissionId = await createProductionSubmission({
        project_id: projectId,
        submission_date: reportDate,
        report_type: 'daily_production',
        segment_ids: JSON.stringify(allSegIds),
        device_ids: null,
        line_items_json: JSON.stringify(lineItems),
        total_footage: totalFootage,
        subtotal: subtotal,
        status: 'Submitted',
        submitted_at: new Date().toISOString(),
        submitted_by: user?.email || 'admin',
        submitted_to_email: clientEmail,
        notes: submissionNotes || null,
      });

      if (!submissionId) { showToast('Failed to create submission', 'error'); setSubmitting(false); return; }

      // Mark segments as billed
      if (boringSegIds.length > 0) await markSegmentsBilled(boringSegIds, 'boring', 'Submitted', submissionId);
      if (pullingSegIds.length > 0) await markSegmentsBilled(pullingSegIds, 'pulling', 'Submitted', submissionId);

      // Generate and send email
      const emailHtml = generateProductionEmailHtml(lineItems, project, reportDate, subtotal, totalFootage, submissionNotes);
      await sendEmailNotification(clientEmail, `Daily Production Report — ${project?.name || projectId} — ${reportDate}`, emailHtml);

      // Refresh data
      const [newData, newSubs] = await Promise.all([
        loadFullProject(projectId),
        loadProductionSubmissions(projectId),
      ]);
      setSegments(newData.segments || []);
      setSubmissions(newSubs || []);
      setSubmissionNotes('');
      setShowPreview(false);
      setActiveTab('submissions');

      showToast(`Production report sent to ${clientEmail}`);
    } catch (err) {
      console.error('Submit to client failed:', err);
      showToast('Submission failed: ' + err.message, 'error');
    }
    setSubmitting(false);
  };

  const handleLogApproval = async (submissionId) => {
    setActionLoading(true);
    try {
      await updateSubmissionField(submissionId, 'status', 'Approved');
      await updateSubmissionField(submissionId, 'metronet_approved_at', new Date().toISOString());
      await updateSubmissionField(submissionId, 'metronet_approved_by', clientName);

      // Update segment billing statuses
      const sub = submissions.find(s => s.submission_id === submissionId);
      if (sub?.segment_ids) {
        const segIds = JSON.parse(sub.segment_ids);
        const items = sub.line_items_json ? JSON.parse(sub.line_items_json) : [];
        const boringIds = items.filter(li => li.phase === 'boring').map(li => li.segment_id);
        const pullingIds = items.filter(li => li.phase === 'pulling').map(li => li.segment_id);
        if (boringIds.length > 0) await markSegmentsBilled(boringIds, 'boring', 'Approved', submissionId);
        if (pullingIds.length > 0) await markSegmentsBilled(pullingIds, 'pulling', 'Approved', submissionId);
      }

      const newSubs = await loadProductionSubmissions(projectId);
      setSubmissions(newSubs || []);
      setApprovalModal(null);
      showToast('Client approval logged');
    } catch (err) {
      showToast('Failed to log approval: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  const handleLogPayment = async (submissionId) => {
    if (!paymentAmount) { showToast('Enter payment amount', 'error'); return; }
    setActionLoading(true);
    try {
      await updateSubmissionField(submissionId, 'status', 'Paid');
      await updateSubmissionField(submissionId, 'payment_received_at', new Date().toISOString());
      await updateSubmissionField(submissionId, 'payment_amount', paymentAmount);
      if (paymentRef) await updateSubmissionField(submissionId, 'payment_reference', paymentRef);
      if (paymentInvNum) await updateSubmissionField(submissionId, 'payment_invoice_number', paymentInvNum);

      // Update segment billing statuses
      const sub = submissions.find(s => s.submission_id === submissionId);
      if (sub?.segment_ids) {
        const items = sub.line_items_json ? JSON.parse(sub.line_items_json) : [];
        const boringIds = items.filter(li => li.phase === 'boring').map(li => li.segment_id);
        const pullingIds = items.filter(li => li.phase === 'pulling').map(li => li.segment_id);
        if (boringIds.length > 0) await markSegmentsBilled(boringIds, 'boring', 'Paid', submissionId);
        if (pullingIds.length > 0) await markSegmentsBilled(pullingIds, 'pulling', 'Paid', submissionId);
      }

      const [newData, newSubs] = await Promise.all([
        loadFullProject(projectId),
        loadProductionSubmissions(projectId),
      ]);
      setSegments(newData.segments || []);
      setSubmissions(newSubs || []);
      setPaymentModal(null);
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentInvNum('');
      showToast('Payment recorded');
    } catch (err) {
      showToast('Failed to log payment: ' + err.message, 'error');
    }
    setActionLoading(false);
  };

  const handlePrint = () => {
    setIsPrintMode(true);
    setTimeout(() => { window.print(); setIsPrintMode(false); }, 300);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} color={accent} style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, backgroundColor: toast.type === 'error' ? '#dc3545' : '#4CAF50', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      {!isPrintMode && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setCurrentPage('admin-projects')} aria-label="Back" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}><ArrowLeft size={20} /></button>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Daily Production & Billing</h1>
              <div style={{ fontSize: 13, color: muted }}>{project?.name || 'Project'} {project?.wo_number ? `| WO: ${project.wo_number}` : ''}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: `${accent}15`, color: accent, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              <Printer size={16} /> Print
            </button>
            {setDarkMode && (
              <button onClick={() => setDarkMode(!darkMode)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 8 }}>
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      {!isPrintMode && (
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${border}` }}>
          {[
            { id: 'report', label: 'Daily Report', icon: <FileText size={14} /> },
            { id: 'submit', label: `Submit to ${clientName} (${billableSegments.length})`, icon: <Send size={14} /> },
            { id: 'submissions', label: `Submission History (${submissions.length})`, icon: <Clock size={14} /> },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '12px 20px', background: 'none', border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${accent}` : '2px solid transparent',
              color: activeTab === tab.id ? accent : muted, fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      {!isPrintMode && (activeTab === 'report' || activeTab === 'submit') && (
        <div style={{ display: 'flex', gap: 12, padding: '12px 20px', flexWrap: 'wrap', borderBottom: `1px solid ${border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} color={muted} />
            <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13 }} />
          </div>
          <select value={filterContractor} onChange={(e) => setFilterContractor(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13 }}>
            <option value="all">All Contractors</option>
            {contractors.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13 }}>
            <option value="all">All Sections</option>
            {sections.map(s => <option key={s} value={s}>Section {s}</option>)}
          </select>
        </div>
      )}

      <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>

        {/* ====== TAB: DAILY REPORT ====== */}
        {activeTab === 'report' && (
          <>
            {isPrintMode && (
              <div style={{ textAlign: 'center', marginBottom: 24, color: '#000' }}>
                <h1 style={{ margin: 0, fontSize: 20 }}>LYT Communications - Daily Production Report</h1>
                <p style={{ margin: '4px 0', fontSize: 14 }}>{project?.name || 'Project'} | PO: {project?.wo_number || 'N/A'}</p>
                <p style={{ margin: '4px 0', fontSize: 14 }}>Date: {new Date(reportDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            )}

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Boring', value: `${summary.boringFootage.toLocaleString()} LF`, sub: `${summary.boringSegments} segments`, icon: <Ruler size={18} />, color: '#FFB800' },
                { label: 'Pulling', value: `${summary.pullingFootage.toLocaleString()} LF`, sub: `${summary.pullingSegments} segments`, icon: <MapPin size={18} />, color: '#2196F3' },
                { label: 'Splicing', value: `${summary.spliceCount} points`, sub: `${activeSplices.filter(s => s.status === 'QC Approved').length} approved`, icon: <Users size={18} />, color: '#9C27B0' },
                { label: 'Daily Total', value: `$${summary.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, sub: 'billable value', icon: <DollarSign size={18} />, color: '#4CAF50' },
              ].map((card, i) => (
                <div key={i} style={{ backgroundColor: isPrintMode ? '#f8f8f8' : cardBg, borderRadius: 10, padding: 16, border: `1px solid ${isPrintMode ? '#ddd' : border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ color: card.color }}>{card.icon}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isPrintMode ? '#666' : muted, textTransform: 'uppercase' }}>{card.label}</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: isPrintMode ? '#000' : text }}>{card.value}</div>
                  <div style={{ fontSize: 12, color: isPrintMode ? '#888' : muted, marginTop: 2 }}>{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Segment Activity Table */}
            {activeSegments.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Segment Activity</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ backgroundColor: isPrintMode ? '#f0f0f0' : (darkMode ? '#1e3a5f' : '#f1f5f9') }}>
                        {['Segment', 'Section', 'Street', 'Footage', 'Boring', 'Pulling', 'Contractor'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${isPrintMode ? '#ccc' : border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeSegments.map(seg => (
                        <tr key={seg.segment_id} style={{ borderBottom: `1px solid ${isPrintMode ? '#eee' : border}` }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: accent }}>{seg.contractor_id || seg.segment_id}</td>
                          <td style={{ padding: '10px 12px' }}>{seg.section}</td>
                          <td style={{ padding: '10px 12px', color: muted }}>{seg.street || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>{seg.footage} LF</td>
                          <td style={{ padding: '10px 12px' }}><StatusBadge status={seg.boring_status} isPrint={isPrintMode} /></td>
                          <td style={{ padding: '10px 12px' }}><StatusBadge status={seg.pulling_status} isPrint={isPrintMode} /></td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: muted }}>{seg.boring_assigned_to || seg.pulling_assigned_to || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeSegments.length === 0 && activeSplices.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: muted }}>
                <FileText size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No Activity Recorded</div>
                <div style={{ fontSize: 13 }}>No segments had activity on {new Date(reportDate).toLocaleDateString()}</div>
              </div>
            )}
          </>
        )}

        {/* ====== TAB: SUBMIT TO CLIENT ====== */}
        {activeTab === 'submit' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Submit Production to {clientName}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: muted }}>
                <CheckCircle size={14} color="#4CAF50" />
                {billableSegments.length} QC-approved segments ready
              </div>
            </div>

            {billableSegments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: muted }}>
                <CheckCircle size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5, color: '#4CAF50' }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>All Caught Up</div>
                <div style={{ fontSize: 13 }}>No unbilled QC-approved segments. All production has been submitted.</div>
              </div>
            ) : (
              <>
                {/* Submission Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                  <div style={{ backgroundColor: cardBg, borderRadius: 10, padding: 16, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: muted, textTransform: 'uppercase', marginBottom: 4 }}>Segments</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{billableSegments.length}</div>
                  </div>
                  <div style={{ backgroundColor: cardBg, borderRadius: 10, padding: 16, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: muted, textTransform: 'uppercase', marginBottom: 4 }}>Total Footage</div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{totalFootage.toLocaleString()} LF</div>
                  </div>
                  <div style={{ backgroundColor: cardBg, borderRadius: 10, padding: 16, border: `1px solid ${border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: muted, textTransform: 'uppercase', marginBottom: 4 }}>Billable Amount</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#4CAF50' }}>${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>

                {/* Line Items Preview */}
                <div style={{ marginBottom: 20 }}>
                  <button onClick={() => setShowPreview(!showPreview)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: accent, cursor: 'pointer', fontWeight: 600, fontSize: 13, padding: 0 }}>
                    {showPreview ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {showPreview ? 'Hide' : 'Show'} Line Items ({lineItems.length})
                  </button>

                  {showPreview && (
                    <div style={{ overflowX: 'auto', marginTop: 12 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                            {['Code', 'Description', 'Qty', 'UOM', 'Rate', 'Total'].map(h => (
                              <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: `2px solid ${border}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((li, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{li.code}</td>
                              <td style={{ padding: '10px 12px' }}>{li.description}</td>
                              <td style={{ padding: '10px 12px' }}>{li.qty.toLocaleString()}</td>
                              <td style={{ padding: '10px 12px', color: muted }}>{li.uom}</td>
                              <td style={{ padding: '10px 12px' }}>${li.rate.toFixed(2)}</td>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>${li.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                          <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                            <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 700, textAlign: 'right' }}>TOTAL</td>
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: '#4CAF50' }}>${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Email & Notes */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 6 }}>Send To (Email)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Mail size={14} color={muted} />
                      <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder={`billing@${clientName.toLowerCase().replace(/\s+/g, '')}.com`} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13 }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 6 }}>Notes (optional)</label>
                    <input type="text" value={submissionNotes} onChange={e => setSubmissionNotes(e.target.value)} placeholder="Any notes for this submission..." style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* Submit Button */}
                <button onClick={handleSubmitToClient} disabled={submitting || lineItems.length === 0} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                  padding: '14px 24px', backgroundColor: submitting ? muted : '#4CAF50', color: '#fff',
                  border: 'none', borderRadius: 10, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 15,
                }}>
                  {submitting ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                  {submitting ? 'Sending...' : `Submit ${lineItems.length} Line Items to ${clientName}`}
                </button>
              </>
            )}
          </>
        )}

        {/* ====== TAB: SUBMISSION HISTORY ====== */}
        {activeTab === 'submissions' && (
          <>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Submission History</h3>

            {submissions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: muted }}>
                <Clock size={48} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No Submissions Yet</div>
                <div style={{ fontSize: 13 }}>Submit your first production report from the "Submit to {clientName}" tab.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {submissions.map(sub => {
                  const isExpanded = expandedSubmission === sub.submission_id;
                  const items = sub.line_items_json ? JSON.parse(sub.line_items_json) : [];
                  const segIds = sub.segment_ids ? JSON.parse(sub.segment_ids) : [];

                  return (
                    <div key={sub.submission_id} style={{ backgroundColor: cardBg, borderRadius: 10, border: `1px solid ${border}`, overflow: 'hidden' }}>
                      {/* Header row */}
                      <div onClick={() => setExpandedSubmission(isExpanded ? null : sub.submission_id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <BillingStatusBadge status={sub.status} />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{new Date(sub.submission_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                            <div style={{ fontSize: 12, color: muted }}>{segIds.length} segments | {parseFloat(sub.total_footage || 0).toLocaleString()} LF</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>${parseFloat(sub.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                            {sub.payment_amount && <div style={{ fontSize: 12, color: '#4CAF50' }}>Paid: ${parseFloat(sub.payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
                          </div>
                          {isExpanded ? <ChevronUp size={16} color={muted} /> : <ChevronDown size={16} color={muted} />}
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${border}` }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '12px 0' }}>
                            <InfoItem label="Submitted" value={sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '—'} />
                            <InfoItem label="Submitted By" value={sub.submitted_by || '—'} />
                            <InfoItem label="Sent To" value={sub.submitted_to_email || '—'} />
                            <InfoItem label="Client Approved" value={sub.metronet_approved_at ? new Date(sub.metronet_approved_at).toLocaleString() : '—'} />
                            <InfoItem label="Payment Received" value={sub.payment_received_at ? new Date(sub.payment_received_at).toLocaleString() : '—'} />
                            <InfoItem label="Payment Ref" value={sub.payment_reference || '—'} />
                            <InfoItem label="Client Invoice #" value={sub.payment_invoice_number || '—'} />
                            {sub.notes && <InfoItem label="Notes" value={sub.notes} />}
                          </div>

                          {/* Line items */}
                          {items.length > 0 && (
                            <div style={{ overflowX: 'auto', marginTop: 8 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ backgroundColor: darkMode ? '#1e3a5f' : '#f1f5f9' }}>
                                    {['Code', 'Description', 'Qty', 'Rate', 'Total'].map(h => (
                                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, borderBottom: `1px solid ${border}` }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((li, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{li.code}</td>
                                      <td style={{ padding: '8px 10px' }}>{li.description}</td>
                                      <td style={{ padding: '8px 10px' }}>{li.qty}</td>
                                      <td style={{ padding: '8px 10px' }}>${parseFloat(li.rate).toFixed(2)}</td>
                                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>${parseFloat(li.total).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            {sub.status === 'Submitted' && (
                              <button onClick={() => setApprovalModal(sub.submission_id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <Check size={14} /> Log Client Approval
                              </button>
                            )}
                            {(sub.status === 'Approved' || sub.status === 'Submitted') && (
                              <button onClick={() => { setPaymentModal(sub.submission_id); setPaymentAmount(sub.subtotal || ''); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <CreditCard size={14} /> Log Payment Received
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Approval Confirmation Modal */}
      {approvalModal && (
        <Modal onClose={() => setApprovalModal(null)} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Confirm Client Approval</h3>
          <p style={{ fontSize: 14, color: muted, margin: '0 0 20px' }}>Mark this production submission as approved by {clientName}? This will update all included segments to "Approved" billing status.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setApprovalModal(null)} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={() => handleLogApproval(approvalModal)} disabled={actionLoading} style={{ padding: '10px 20px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Confirm Approval'}
            </button>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <Modal onClose={() => setPaymentModal(null)} darkMode={darkMode}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Record Payment Received</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Payment Amount *</label>
              <input type="number" step="0.01" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Payment Reference (check #, ACH ref, etc.)</label>
              <input type="text" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="CHK-12345" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: muted, display: 'block', marginBottom: 4 }}>Client Invoice Number</label>
              <input type="text" value={paymentInvNum} onChange={e => setPaymentInvNum(e.target.value)} placeholder="INV-2026-001" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${border}`, backgroundColor: cardBg, color: text, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setPaymentModal(null)} style={{ padding: '10px 20px', backgroundColor: darkMode ? '#1e3a5f' : '#e2e8f0', color: text, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
            <button onClick={() => handleLogPayment(paymentModal)} disabled={actionLoading || !paymentAmount} style={{ padding: '10px 20px', backgroundColor: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              {actionLoading ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </Modal>
      )}

      {/* Print styles */}
      <style>{`@media print { body { background: white !important; color: black !important; } * { color: black !important; } }`}</style>

      {/* Version */}
      <div onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }} style={{ position: 'fixed', bottom: 4, right: 8, fontSize: 9, color: showVersion ? (darkMode ? '#fff' : '#333') : 'transparent', opacity: showVersion ? 0.5 : 1, userSelect: 'none', cursor: 'default' }}>DailyProductionReport v2.0.0</div>
    </div>
  );
}

// ========== HELPER COMPONENTS ==========

function StatusBadge({ status, isPrint }) {
  const colors = {
    'QC Approved': { bg: '#4CAF5020', text: '#4CAF50', print: '✅' },
    'Complete': { bg: '#2196F320', text: '#2196F3', print: '✔' },
    'In Progress': { bg: '#FFB80020', text: '#FFB800', print: '🔄' },
    'Issue': { bg: '#FF980020', text: '#FF9800', print: '⚠' },
    'Not Started': { bg: '#9E9E9E20', text: '#9E9E9E', print: '—' },
  };
  const c = colors[status] || colors['Not Started'];
  if (isPrint) return <span>{c.print} {status}</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, backgroundColor: c.bg, color: c.text, fontSize: 12, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.text }} />
      {status}
    </span>
  );
}

function BillingStatusBadge({ status }) {
  const colors = {
    'Draft': { bg: '#9E9E9E20', text: '#9E9E9E' },
    'Submitted': { bg: '#FFB80020', text: '#FFB800' },
    'Approved': { bg: '#2196F320', text: '#2196F3' },
    'Paid': { bg: '#4CAF5020', text: '#4CAF50' },
  };
  const c = colors[status] || colors['Draft'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, backgroundColor: c.bg, color: c.text, fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.text }} />
      {status}
    </span>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#8892b0', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}

function Modal({ children, onClose, darkMode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: darkMode ? '#112240' : '#ffffff', borderRadius: 12, padding: 24, maxWidth: 480, width: '90%', position: 'relative', color: darkMode ? '#ffffff' : '#1e293b' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: darkMode ? '#8892b0' : '#64748b' }}><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}

// ========== EMAIL GENERATOR ==========

function generateProductionEmailHtml(lineItems, project, reportDate, subtotal, totalFootage, notes) {
  const dateFormatted = new Date(reportDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const rows = lineItems.map(li => `
    <tr>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;font-weight:600;">${li.code}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;">${li.description}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">${li.qty.toLocaleString()}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:center;">${li.uom}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;">$${parseFloat(li.rate).toFixed(2)}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-weight:600;">$${parseFloat(li.total).toFixed(2)}</td>
    </tr>
  `).join('');

  return `
    <p style="margin:0 0 16px;"><strong>Project:</strong> ${project?.name || 'N/A'} | <strong>WO:</strong> ${project?.wo_number || 'N/A'}</p>
    <p style="margin:0 0 16px;"><strong>Date:</strong> ${dateFormatted}</p>
    <p style="margin:0 0 16px;"><strong>Total Footage:</strong> ${totalFootage.toLocaleString()} LF | <strong>Line Items:</strong> ${lineItems.length}</p>
    ${notes ? `<p style="margin:0 0 16px;"><strong>Notes:</strong> ${notes}</p>` : ''}

    <table style="width:100%;border-collapse:collapse;font-size:13px;margin:16px 0;">
      <thead>
        <tr style="background:#f0f4f8;">
          <th style="padding:10px;text-align:left;border-bottom:2px solid #ccc;">Code</th>
          <th style="padding:10px;text-align:left;border-bottom:2px solid #ccc;">Description</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Qty</th>
          <th style="padding:10px;text-align:center;border-bottom:2px solid #ccc;">UOM</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Rate</th>
          <th style="padding:10px;text-align:right;border-bottom:2px solid #ccc;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        <tr style="background:#f0f4f8;font-weight:700;">
          <td colspan="5" style="padding:10px;text-align:right;">TOTAL</td>
          <td style="padding:10px;text-align:right;color:#2e7d32;">$${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>

    <p style="margin:16px 0 0;font-size:12px;color:#999;">Please confirm receipt and approval of this production report.</p>
  `;
}

export default DailyProductionReport;
